/**
 * A student's program (owner decision 2026-11-03): the same choices as signing up in the app, wherever the
 * administration adds a student. What it means for the plan is worked out on the server.
 */
import { useQuery } from "@tanstack/react-query";
import type { RegistrationProgram, SignupKind, StudyPeriod, SurahsResponse, Weekday } from "@mubin/contracts";
import { api } from "../lib/api";
import { Field } from "./ui";

type Unit = "page" | "rub" | "juz";
export interface ProgramForm {
  kind: SignupKind | "";
  startSurah: string;
  startAyah: string;
  direction: "backward" | "forward" | "";
  daily: string;
  fromSurah: string;
  fromAyah: string;
  toSurah: string;
  toAyah: string;
  review: string;
  days: string;
  period: StudyPeriod | "";
}
export const emptyProgram = (): ProgramForm => ({
  kind: "",
  startSurah: "",
  startAyah: "1",
  direction: "",
  daily: "1",
  fromSurah: "",
  fromAyah: "1",
  toSurah: "",
  toAyah: "",
  review: "p1",
  days: "",
  period: "",
});

const KINDS: { value: SignupKind; label: string }[] = [
  { value: "memorize", label: "حفظ فقط" },
  { value: "memorize_review", label: "حفظ ومراجعة تراكمية" },
  { value: "memorize_review_far", label: "حفظ ومراجعة تراكمية وبعيدة" },
  { value: "review", label: "مراجعة فقط" },
];
const DAILY: Record<string, { label: string; amount: number }> = {
  "0.25": { label: "ربع وجه", amount: 0.25 },
  "0.5": { label: "نصف وجه", amount: 0.5 },
  "1": { label: "وجه", amount: 1 },
  "1.5": { label: "وجه ونصف", amount: 1.5 },
  "2": { label: "وجهان", amount: 2 },
  "3": { label: "٣ أوجه", amount: 3 },
};
const REVIEW: Record<string, { label: string; amount: number; unit: Unit }> = {
  p1: { label: "وجه", amount: 1, unit: "page" },
  p2: { label: "وجهان", amount: 2, unit: "page" },
  r1: { label: "ربع حزب", amount: 1, unit: "rub" },
  r2: { label: "نصف حزب", amount: 2, unit: "rub" },
  r4: { label: "حزب", amount: 4, unit: "rub" },
  j1: { label: "جزء", amount: 1, unit: "juz" },
};
const DAYS: Record<string, { label: string; days: Weekday[] }> = {
  "sun-wed": { label: "من الأحد إلى الأربعاء", days: ["sun", "mon", "tue", "wed"] },
  "sun-thu": { label: "من الأحد إلى الخميس", days: ["sun", "mon", "tue", "wed", "thu"] },
  all: { label: "كل أيام الأسبوع", days: ["sat", "sun", "mon", "tue", "wed", "thu", "fri"] },
  "no-fri": { label: "كل الأيام عدا الجمعة", days: ["sat", "sun", "mon", "tue", "wed", "thu"] },
};
const PERIODS: { value: StudyPeriod; label: string }[] = [
  { value: "fajr", label: "بعد الفجر" },
  { value: "dhuhr", label: "الظهر" },
  { value: "asr", label: "العصر" },
  { value: "maghrib", label: "المغرب" },
  { value: "isha", label: "العشاء" },
];

/** «وجه»، «وجه ونصف»، «وجهان»، «٣ أوجه». */
const pages = (n: number) =>
  n === 1
    ? "وجه"
    : n === 1.5
      ? "وجه ونصف"
      : n === 2
        ? "وجهان"
        : Number.isInteger(n) && n <= 10
          ? `${n.toLocaleString("ar-SA")} أوجه`
          : `${n.toLocaleString("ar-SA")} وجه`;

const memorizes = (k: ProgramForm["kind"]) => !!k && k !== "review";
const reviews = (k: ProgramForm["kind"]) => k === "memorize_review_far" || k === "review";

/** The program to send, or null while something is missing. */
export function programOf(f: ProgramForm): RegistrationProgram | null {
  if (!f.kind || !f.days || !f.period) return null;
  const p: RegistrationProgram = { kind: f.kind, studyDays: DAYS[f.days]!.days, period: f.period };
  if (memorizes(f.kind)) {
    if (!f.startSurah || !f.direction || !Number(f.startAyah)) return null;
    p.start = { surah: Number(f.startSurah), ayah: Number(f.startAyah) };
    p.direction = f.direction;
    p.daily = { amount: DAILY[f.daily]!.amount, unit: "page" };
  }
  if (reviews(f.kind)) {
    if (!f.fromSurah || !f.toSurah || !Number(f.fromAyah) || !Number(f.toAyah)) return null;
    const r = REVIEW[f.review]!;
    p.review = {
      from: { surah: Number(f.fromSurah), ayah: Number(f.fromAyah) },
      to: { surah: Number(f.toSurah), ayah: Number(f.toAyah) },
      daily: { amount: r.amount, unit: r.unit },
    };
  }
  return p;
}

export function ProgramFields({
  value: f,
  onChange,
  reviewMultiple,
}: {
  value: ProgramForm;
  onChange(v: ProgramForm): void;
  reviewMultiple: number;
}) {
  const surahs = useQuery({
    queryKey: ["quran-surahs"],
    queryFn: () => api<SurahsResponse>("/v1/quran/surahs"),
    staleTime: Infinity,
  });
  const list = surahs.data?.surahs ?? [];
  const count = (s: string) => list.find((x) => String(x.id) === s)?.ayahCount ?? 286;
  const set = (patch: Partial<ProgramForm>) => onChange({ ...f, ...patch });
  const surahSelect = (id: string, value: string, change: (v: string) => void) => (
    <select id={id} value={value} onChange={(e) => change(e.target.value)} required>
      <option value="">اختر السورة</option>
      {list.map((s) => (
        <option key={s.id} value={s.id}>
          {s.id}. {s.nameAr}
        </option>
      ))}
    </select>
  );
  const ayah = (id: string, label: string, surah: string, value: string, change: (v: string) => void) => (
    <Field id={id} label={`${label} (١–${count(surah).toLocaleString("ar-SA")})`}>
      <input
        id={id}
        type="number"
        min={1}
        max={count(surah)}
        value={value}
        onChange={(e) => change(e.target.value)}
        required
        style={{ width: 120 }}
      />
    </Field>
  );
  const cumulative = Math.round(DAILY[f.daily]!.amount * reviewMultiple * 100) / 100;

  return (
    <fieldset className="program">
      <legend>خطة الطالب</legend>
      <Field id="pg-kind" label="نوع الخطة">
        <select
          id="pg-kind"
          value={f.kind}
          onChange={(e) => set({ kind: e.target.value as SignupKind })}
          required
        >
          <option value="">اختر</option>
          {KINDS.map((k) => (
            <option key={k.value} value={k.value}>
              {k.label}
            </option>
          ))}
        </select>
      </Field>
      {memorizes(f.kind) && (
        <>
          <div className="form-row">
            <Field id="pg-start" label="يبدأ الحفظ من سورة">
              {surahSelect("pg-start", f.startSurah, (startSurah) => set({ startSurah, startAyah: "1" }))}
            </Field>
            {ayah("pg-start-ayah", "من الآية", f.startSurah, f.startAyah, (startAyah) => set({ startAyah }))}
          </div>
          <div className="form-row">
            <Field id="pg-dir" label="اتجاه الحفظ">
              <select
                id="pg-dir"
                value={f.direction}
                onChange={(e) => set({ direction: e.target.value as ProgramForm["direction"] })}
                required
              >
                <option value="">اختر</option>
                <option value="backward">باتجاه البقرة (من الناس)</option>
                <option value="forward">باتجاه الناس (من الفاتحة)</option>
              </select>
            </Field>
            <Field id="pg-daily" label="مقدار الحفظ اليومي">
              <select id="pg-daily" value={f.daily} onChange={(e) => set({ daily: e.target.value })}>
                {Object.entries(DAILY).map(([k, d]) => (
                  <option key={k} value={k}>
                    {d.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>
        </>
      )}
      {(f.kind === "memorize_review" || f.kind === "memorize_review_far") && (
        <p className="notice small">
          المراجعة التراكمية: {pages(cumulative)} يوميًا ({reviewMultiple.toLocaleString("ar-SA")} أضعاف
          الحفظ)، تدور على ما يحفظه في هذه الخطة من أوله حتى تصل الجديد، ثم تبدأ من الأول.
        </p>
      )}
      {reviews(f.kind) && (
        <>
          <p className="muted small">
            {f.kind === "review" ? "المقطع الذي يراجعه" : "المراجعة البعيدة: ما يحفظه من قبل"}
          </p>
          <div className="form-row">
            <Field id="pg-from" label="من سورة">
              {surahSelect("pg-from", f.fromSurah, (fromSurah) => set({ fromSurah, fromAyah: "1" }))}
            </Field>
            {ayah("pg-from-ayah", "من الآية", f.fromSurah, f.fromAyah, (fromAyah) => set({ fromAyah }))}
          </div>
          <div className="form-row">
            <Field id="pg-to" label="إلى سورة">
              {surahSelect("pg-to", f.toSurah, (toSurah) => set({ toSurah, toAyah: String(count(toSurah)) }))}
            </Field>
            {ayah("pg-to-ayah", "إلى الآية", f.toSurah, f.toAyah, (toAyah) => set({ toAyah }))}
          </div>
          <Field id="pg-review" label="مقدار المراجعة اليومي">
            <select id="pg-review" value={f.review} onChange={(e) => set({ review: e.target.value })}>
              {Object.entries(REVIEW).map(([k, r]) => (
                <option key={k} value={k}>
                  {r.label}
                </option>
              ))}
            </select>
          </Field>
        </>
      )}
      <div className="form-row">
        <Field id="pg-days" label="أيام التسميع">
          <select id="pg-days" value={f.days} onChange={(e) => set({ days: e.target.value })} required>
            <option value="">اختر</option>
            {Object.entries(DAYS).map(([k, d]) => (
              <option key={k} value={k}>
                {d.label}
              </option>
            ))}
          </select>
        </Field>
        <Field id="pg-period" label="الوقت المفضل" hint="رغبة الطالب، لا موعد الحلقة">
          <select
            id="pg-period"
            value={f.period}
            onChange={(e) => set({ period: e.target.value as StudyPeriod })}
            required
          >
            <option value="">اختر</option>
            {PERIODS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
        </Field>
      </div>
    </fieldset>
  );
}
